/** Polling com pausa por aba escondida e backoff progressivo.
 *
 *  Nasceu de medir o que as telas custavam: acompanhar um upload de 393 s
 *  na página de detalhe eram ~628 requisições, e uma aba esquecida batia
 *  no servidor para sempre. Os pontos de polling do app repetiam a mesma
 *  lógica com a constante de intervalo copiada em cada um (eram quatro
 *  quando o hook nasceu; em 2026-10-07 são dez — entre eles a barra
 *  lateral, upload, relatório, importação, lote, reprocessamento e o
 *  sino, que roda em toda tela com cadência própria).
 *
 *  Três regras, e as três existem por um motivo medido:
 *
 *  - **Aba escondida não pergunta nada.** Ninguém está olhando. Ao voltar,
 *    recarrega NA HORA em vez de esperar o próximo tick — senão a tela
 *    ficaria até um intervalo inteiro mostrando dado velho.
 *  - **Backoff enquanto nada muda.** Um upload esperando atrás de um
 *    arquivo grande fazia a mesma pergunta 157 vezes. O intervalo sobe
 *    2,5s → 5s → 10s depois de alguns ticks idênticos e VOLTA na hora que
 *    algo muda. Quem decide "mudou" é quem chama, via `signature`.
 *  - **Pausa quando o servidor pede** (`pausedUntil`, 2026-09-10). O
 *    backoff por assinatura até atenua um 429 — o estado não muda, a
 *    assinatura não muda —, mas só depois de 4 batidas, e com teto de
 *    10 s: contra um `Retry-After: 60` são ~8 batidas inúteis por
 *    minuto POR TELA, cada uma custando outro 429.
 *
 *  **`onTick` continua `() => void`, e isso é deliberado.** O hook nunca
 *  enxerga o erro: cada chamador tem o próprio `try/catch` que grava
 *  estado, e vários desses loaders servem também ao carregamento inicial
 *  e a outros botões. Fazer o hook ler a falha exigiria que os seis
 *  relançassem — mudança grande, em código que já funciona, pra chegar
 *  onde `pausedUntil` chega de forma declarativa: quem tomou o 429 já
 *  está num `catch`, e de lá informa até quando esperar.
 *
 *  Não substitui o carregamento inicial: o hook só agenda repetições. */

import { useEffect, useRef, useState } from "react";
import { ApiError } from "@/shared/api/http";

/** Cadência base. Casada com a do worker, que grava progresso a cada 100
 *  linhas ou 2,0 s (`_PROGRESS_FLUSH_SECONDS` no upload_orchestrator) —
 *  perguntar mais rápido que isso não traz dado novo. */
export const POLL_INTERVAL_MS = 2500;

/** O `pausedUntil` que um `catch` deve gravar a partir da falha que
 *  pegou: o instante até o qual o servidor pediu pra esperar (um 429
 *  com `Retry-After`), ou `null` pra qualquer outra falha — que não é
 *  motivo pra parar de perguntar.
 *
 *  Dono único desta conta (2026-10-25, achado 25 da revisão de
 *  2026-10-24): até então ela estava escrita à mão nos dois pollers da
 *  tela de menções e FALTAVA em três (o contador do menu, a Visão Geral
 *  e o painel de importação), que batiam no servidor a cada tick depois
 *  de um 429 — a quarta vez desta classe de laço. */
export function retryPauseFrom(failure: unknown): number | null {
  if (failure instanceof ApiError && failure.retryAfterSeconds) {
    return Date.now() + failure.retryAfterSeconds * 1000;
  }
  return null;
}

/** Degraus do backoff, em múltiplos do intervalo base. */
const BACKOFF_STEPS = [1, 2, 4];
/** Ticks sem mudança até subir um degrau. */
const TICKS_PER_STEP = 4;

type Options = {
  /** Liga/desliga. `false` não agenda nada e zera o backoff. */
  enabled: boolean;
  /** O que rodar a cada tick. */
  onTick: () => void;
  /** Resumo do estado observado. Mudou → volta pra cadência base; igual
   *  por vários ticks → sobe o degrau. Omitir desliga o backoff. */
  signature?: string;
  /** Instante (epoch ms) até o qual NÃO se pergunta nada. Vem do
   *  `retryAfterSeconds` de um `ApiError` 429, no `catch` de quem
   *  chama. `null`/omitido = sem pausa. */
  pausedUntil?: number | null;
  intervalMs?: number;
};

export function usePolling({
  enabled,
  onTick,
  signature,
  pausedUntil = null,
  intervalMs = POLL_INTERVAL_MS
}: Options): void {
  // O callback vive numa ref pra não recriar o timer a cada render —
  // `onTick` costuma ser uma arrow inline em quem chama.
  const tickRef = useRef(onTick);
  tickRef.current = onTick;

  const [step, setStep] = useState(0);
  const idleTicksRef = useRef(0);
  const lastSignatureRef = useRef(signature);

  useEffect(() => {
    if (signature === undefined) return;
    if (signature !== lastSignatureRef.current) {
      lastSignatureRef.current = signature;
      idleTicksRef.current = 0;
      setStep(0);
    }
  }, [signature]);

  useEffect(() => {
    if (!enabled) {
      idleTicksRef.current = 0;
      setStep(0);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;

    let timer: number | undefined;
    // Temporizador SEPARADO pro fim da pausa. Ele precisa ser cancelado
    // junto com o outro: um `resume` sobrevivente dispararia com a aba
    // escondida e religaria o polling que a aba escondida desligou —
    // o inverso exato do bug que o hook existe pra evitar.
    let resume: number | undefined;

    const clear = () => {
      if (timer !== undefined) window.clearInterval(timer);
      if (resume !== undefined) window.clearTimeout(resume);
      timer = undefined;
      resume = undefined;
    };

    /** Quanto falta da pausa, em ms. Zero ou negativo = pode perguntar. */
    const restaDaPausa = () => (pausedUntil ?? 0) - Date.now();

    const start = () => {
      clear();
      // UMA porta pra "posso ticar agora?", usada pelo arranque, pelo
      // retorno da aba e pelo fim da pausa. Com a decisão espalhada, o
      // caso "voltou pra aba ANTES de a pausa vencer" fica sem quem
      // reagende — e a tela nunca mais atualiza.
      if (document.visibilityState === "hidden") return;
      const falta = restaDaPausa();
      if (falta > 0) {
        resume = window.setTimeout(start, falta);
        return;
      }
      timer = window.setInterval(() => {
        idleTicksRef.current += 1;
        // Sobe um degrau só quando quem chama informa `signature` — sem
        // isso não há como saber se a resposta mudou, e acelerar às cegas
        // atrasaria dado novo.
        if (
          signature !== undefined &&
          idleTicksRef.current >= TICKS_PER_STEP &&
          step < BACKOFF_STEPS.length - 1
        ) {
          idleTicksRef.current = 0;
          setStep(current => current + 1);
        }
        tickRef.current();
      }, intervalMs * BACKOFF_STEPS[step]);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        clear();
        return;
      }
      // Voltou durante a pausa: reagenda pro fim dela, SEM tick — o
      // tick imediato aqui seria mais um 429, e não reagendar deixaria
      // a tela parada pra sempre.
      if (restaDaPausa() > 0) {
        start();
        return;
      }
      // Voltou: busca imediatamente e reinicia do degrau base — o tempo
      // escondido não conta como "nada mudou". (Vale também pra pausa
      // que venceu enquanto a aba estava escondida.)
      idleTicksRef.current = 0;
      setStep(0);
      tickRef.current();
      start();
    };

    start();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clear();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
    // `pausedUntil` nas deps: uma pausa NOVA precisa rearmar tudo, e
    // sem ela o efeito seguiria com o valor do render anterior.
  }, [enabled, intervalMs, step, signature, pausedUntil]);
}
